/**
 * useStagingRoomSync — the async-derived room-set reconcile (MODULE-CONTRACT §10.2,
 * E2). The ONE sanctioned derived write to `productConfigs` in this module.
 *
 * ADD-always + prune-if-seen-and-absent — HUB-OWNED membership signal (no drawer
 * coupling, no `touched` flag). Both gated on `!isAnyRunning` (quiescence, NOT
 * `uploadFinished` — the latter needs `ready.length > 0` and would freeze the
 * reconcile at the empty-feed boundary, `stagingVisuals.ts:104`):
 *
 *   - **ADD:** a feed visual with no room → a default room (idempotent `addRoom`
 *     no-ops on a key that already exists → resumed DTOs + app-wide FileAPI churn
 *     never reach `markConfigDirty`, R2).
 *   - **PRUNE:** a room whose id was SEEN LIVE in the feed this session
 *     (`seenThisSession`, accumulated by the shared `PfV2VisualUploadProvider`
 *     across drawer + catalogue + config) and is now ABSENT from the feed → the
 *     user deleted it (in the drawer, possibly while the config step was
 *     unmounted). Structurally-idempotent `removeRoom`.
 *
 * Why this is F-B1-safe on the collection axis: `seenThisSession` resets on
 * session change and is empty on resume (the feed is empty on resume, §10.4), so a
 * hydrated-on-resume room is NEVER in the seen-set → immune to the prune. A blind
 * feed set-difference prune (killed at the gate, opponent Challenge 1) WOULD have
 * deleted every hydrated room on the first post-resume upload; the seen-gate is
 * exactly what neutralizes that. This is strictly BETTER than the legacy
 * feed-overwrite regen (which dropped hydrated rooms on resume+upload and left
 * stale rooms on delete-to-empty).
 *
 * §10.3 invariant (feed keys are stable gcFilenames matching DTO keys) is what
 * makes ADD-pre-interaction + the seen-gated prune sound — regression-locked in
 * the tests.
 */

import { useEffect } from 'react'

import type { CartItemId } from 'models/purchaseFlowRevamp'
import { ProductKind, STAGING_PRODUCT_KINDS_PROPERTY_TYPES } from 'constants/product'

import { usePfV2VisualUpload, visualsForProductCode } from '../../fileHub'
import { usePfStore } from '../../store'
import { claimedFilenames } from '../../store/slices/cartAttachmentsSlice'
import { useStagingConfig } from './useStagingConfig'
import { makeDefaultRoom } from './stagingRoom.logic'

/**
 * The resolved propertyType is always the RESIDENTIAL_STAGING mapping
 * (STANDARD_CATALOG), exactly like the legacy feed which read
 * `selectedProducts[sentinel].kind`. See OQ-S3: the real variant kind is not
 * consulted in v2; a commercial-staging propertyType is a deferred follow-up
 * (DF-staging-property-type). Note the visual's own tag is now the product code,
 * not the sentinel — the propertyType mapping is unaffected.
 */
const DEFAULT_STAGING_PROPERTY_TYPE =
  STAGING_PRODUCT_KINDS_PROPERTY_TYPES[ProductKind.RESIDENTIAL_STAGING] ?? null

/** Reconciles the file-hub staging visual feed into the DTO room set. */
export function useStagingRoomSync(cartItemId: CartItemId): void {
  const { rooms, addRoom, removeRoom } = useStagingConfig(cartItemId)
  const { visuals, isAnyRunning, seenThisSession } = usePfV2VisualUpload()

  // The FileAPI scope spans the whole flow and now carries one tag per product
  // GROUP, so this line's feed is only the visuals uploaded for ITS product — a
  // raw read would seed rooms from photos uploaded for a different product
  // entirely. The group is the cart line's `shellProductId` (the shell's own
  // `productCode`), matching what the drawer uploaded under.
  const scopeKey = usePfStore(
    (state) => state.items.find((item) => item.id === cartItemId)?.shellProductId ?? null
  )
  const attachments = usePfStore((state) => state.attachments)

  useEffect(() => {
    if (isAnyRunning) return // Gate on quiescence, not uploadFinished

    // Same rule as the drawer: this product's visuals, minus the ones another cart
    // line already owns (a claim is that line's attachment snapshot).
    const claimed = claimedFilenames(attachments, cartItemId)
    const ownVisuals = visualsForProductCode(visuals, scopeKey).filter((v) => !claimed.has(v.id))
    const feedIds = new Set(ownVisuals.map((v) => v.id))

    // ADD — a feed visual with no room → default room. Stable creation order:
    // continue from the current room count so a room's sort order is assigned once
    // and persisted (never re-derived on resume).
    let sortingOrder = Object.keys(rooms).length
    for (const visual of ownVisuals) {
      if (rooms[visual.id]) continue // idempotent — resumed / already-added room
      addRoom(visual.id, makeDefaultRoom(visual.id, visual.productId, sortingOrder, DEFAULT_STAGING_PROPERTY_TYPE))
      sortingOrder += 1
    }

    // PRUNE — a room seen live this session but now gone from the feed (a real
    // delete). Hydrated-on-resume rooms are never in `seenThisSession` → immune.
    for (const roomKey of Object.keys(rooms)) {
      if (seenThisSession.has(roomKey) && !feedIds.has(roomKey)) removeRoom(roomKey)
    }
  }, [isAnyRunning, visuals, scopeKey, attachments, cartItemId, rooms, addRoom, removeRoom, seenThisSession])
}
